import { ColumnDef } from '@tanstack/react-table';
import { useInstagramUserTopPosts } from '@round/platforms/instagram';
import { InstagramTopVideo } from '@round/api';
import { Card, SelectOption, tableHelpers } from '@round/creatorbase-ui';
import styles from './TopPosts.module.css';
import { formatDateShort, numberWithCommas } from 'helpers';
import VirtualizedTable from '../../../components/VirtualizedTable/VirtualizedTable';
import { ClientInfluencerAccount } from '../../../helpers';
import TableSortingSelect from '../../../components/TableSortingSelect/TableSortingSelect';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import TableEmptyState from '../../../components/TableEmptyState/TableEmptyState';

const { SkeletonCell, TruncatedLinkCell } = tableHelpers;
const sortingOptions: SelectOption<keyof InstagramTopVideo>[] = [
    { label: 'Most recent', value: 'create_time' },
    { label: 'Most views', value: 'view_count' },
    { label: 'Most likes', value: 'like_count' },
    { label: 'Most comments', value: 'comment_count' },
];

type Props = {
    account: ClientInfluencerAccount | undefined | null;
    isLoading?: boolean;
};

type InstagramTopPostsSortKey = (typeof sortingOptions)[number]['value'];

const getSortValue = (row: InstagramTopVideo, key: InstagramTopPostsSortKey): number => {
    if (key === 'create_time') {
        if (!row.create_time) {
            return Number.NEGATIVE_INFINITY;
        }

        const timestamp = Date.parse(row.create_time);
        return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
    }

    const value = row[key];
    if (value === null || value === undefined) {
        return Number.NEGATIVE_INFINITY;
    }

    return typeof value === 'number' ? value : Number.NEGATIVE_INFINITY;
};

const InstagramTopPosts = ({ account, isLoading }: Props) => {
    const userId = account?.creatorbase_user_id;

    const {
        data: topPosts,
        isLoading: areTopPostsLoading,
        isError,
    } = useInstagramUserTopPosts({ userId: userId ?? undefined }, { enabled: !!userId && !isLoading });

    const columns: ColumnDef<tableHelpers.WithLoadingRow<InstagramTopVideo>, any>[] = [
        {
            header: 'Description',
            id: 'description',
            meta: { className: styles.descriptionCell },
            size: 300,
            accessorFn: (row) => ({
                href: row.post_url,
                value: row.description || '-',
                tooltipContent: <div className={styles.descriptionCellTooltip}>{row.description || '-'}</div>,
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Date posted',
            id: 'created_time',
            meta: { className: styles.fadedCell },
            accessorFn: ({ create_time }) => (create_time ? formatDateShort(create_time) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => (row.view_count ? numberWithCommas(row.view_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Likes',
            id: 'likes',
            accessorFn: (row) => (row.like_count ? numberWithCommas(row.like_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Comments',
            id: 'comments',
            accessorFn: (row) => (row.comment_count ? numberWithCommas(row.comment_count) : '-'),
            cell: SkeletonCell,
        },
    ];

    const [sorting, setSorting] = useState<InstagramTopPostsSortKey>(sortingOptions[0].value);
    const meta = {
        isLoading: isLoading || areTopPostsLoading,
        isError: isError,
    } as const;

    const rows = tableHelpers.getLoadingRows(topPosts ?? [], { isLoading: meta.isLoading, pageSize: 10 });

    const sortedRows = useMemo(() => {
        if (meta.isLoading) {
            return rows;
        }

        return [...rows].sort((a, b) => {
            if (tableHelpers.isLoadingRow(a) || tableHelpers.isLoadingRow(b)) {
                return 0;
            }

            return getSortValue(b, sorting) - getSortValue(a, sorting);
        });
    }, [meta.isLoading, rows, sorting]);

    const noDataTitle = meta.isError ? 'Something went wrong' : 'No posts found';
    const noDataDescription = meta.isError
        ? 'Failed to load posts. Please try again.'
        : 'No posts found for this creator. Please try again later.';

    return (
        <Card size="sm" variant="borderless" className={styles.cardContainer}>
            <Card.Header className={cn(styles.cardHeader, styles.header)}>
                <Card.Title>Top Posts</Card.Title>
                <TableSortingSelect options={sortingOptions} value={sorting} onChange={setSorting} />
            </Card.Header>
            <Card.Content className={styles.cardContent}>
                <VirtualizedTable
                    meta={meta}
                    data={sortedRows}
                    columns={columns}
                    virtualizerOptions={{
                        count: sortedRows.length,
                        estimateSize: () => 50,
                    }}
                    noDataLabel={
                        <TableEmptyState isError={meta.isError} title={noDataTitle} description={noDataDescription} />
                    }
                />
            </Card.Content>
        </Card>
    );
};

export default InstagramTopPosts;
